/**
 * Keyframe inspector (W2-06; DESIGN.md §4.11 "KEY Rotation · layer Base", §5.6.2, §6.2):
 *  - At (beat field with live echo; relative input moves every selected keyframe), Value in the property's
 *    friendly unit (units toggle in Full, per-item expressions with `v0` / `t` / `s`), colour and text values;
 *  - Jump: "arrive at A then B" (Core.keys.makeJump / setKeyValues part 'in' / 'out'), "Continue from previous"
 *    to remove it; the arrive value is fixed after a Stay segment (hold arrives as a jump, D§5.6.2);
 *  - a single keyframe also shows its outgoing segment (SegmentBody) or that it is the last one;
 *  - Delete (the first keyframe cannot be deleted while others remain: Core's reason is shown).
 */

import { useState } from 'react';
import type { AppServices, InspectorSectionDef, InspectorSectionProps } from '../../../app/registry/types';
import { Core } from '../../../core/commands/index';
import type { ExprProgram } from '../../../core/expr/types';
import { propAppliesTo } from '../../../core/model/props';
import { Units } from '../../../core/model/units';
import type { KeyId } from '../../../core/model/types';
import type { RGB } from '../../../core/util/types';
import { useT } from '../../../i18n/useT';
import type { DocStore } from '../../../state/types';
import { BeatField, Checkbox, ColorField, IconButton, NumberField, Section, TextArea } from '../../../ui';
import { exprKeysShown, moveKeysBy, moveKeysTo } from '../commands';
import { m } from '../inspector.messages';
import { beatEq, fieldOf, fieldOr, rgbEq, sortedIds } from '../model';
import {
  Callout,
  crossScreenText,
  fmtNum,
  Readout,
  Row,
  Title,
  useBeatCtx,
  useSectionBase,
  useStore,
  WithInfo,
} from '../parts';
import s from '../Inspector.module.css';
import { newSeed, useEditKit } from '../tx';
import { FIELD, useFieldProblems } from '../useFieldProblems';
import { commonUnit, unitSetup, useKeyInfos, type KeyInfo } from './keyInfo';
import { propName, SegmentBody } from './SegmentSection';

export function KeySection(props: InspectorSectionProps) {
  const store = useStore(props.services);
  const ids = sortedIds(props.selection.keys);
  if (store === null || ids.length === 0) return null;
  // Keyed on the selection: the jump toggle's local state belongs to one keyframe.
  return <KeyBody key={ids.join(',')} services={props.services} store={store} ids={ids} />;
}

function KeyBody(props: { readonly services: AppServices; readonly store: DocStore; readonly ids: readonly KeyId[] }) {
  const { services, store } = props;
  const t = useT();
  const base = useSectionBase(services, store);
  const beatCtx = useBeatCtx(services, base);
  const infos = useKeyInfos(store, props.ids);
  const ids = infos.map((i) => i.ctx.key.id);
  const edit = useEditKit(services, store, `keys:${ids.join(',')}`);
  const badges = useFieldProblems(services, { keys: ids });
  const [seed, setSeed] = useState(newSeed);
  const [jumpOpen, setJumpOpen] = useState(false);
  const first = infos[0];
  if (first === undefined) return null;
  const single = infos.length === 1 ? first : null;
  const afterExpr = (v: { readonly kind: string; readonly program?: ExprProgram }): void => {
    if (v.kind === 'expr' && v.program?.usesRand === true) setSeed(newSeed());
  };

  const at = fieldOr(
    infos.map((i) => i.ctx.key.t),
    first.ctx.key.t,
    beatEq,
  );
  const types = new Set(infos.map((i) => i.valueType));
  const valueType = types.size === 1 ? first.valueType : 'mixed';
  const { unit, mixed: unitMixed } = commonUnit(infos);
  const setup = unitSetup(t, services, base, unit);
  const isJump = single !== null && single.ctx.key.vIn !== single.ctx.key.vOut;
  const showJump = single !== null && single.ctx.index > 0 && valueType === 'number' && (isJump || jumpOpen);
  const arriveFixed = single?.ctx.prev?.ease.kind === 'hold';
  const ignoredByKind = infos.some((i) => i.ctx.prop !== null && !propAppliesTo(i.ctx.prop, i.kind));

  const header =
    single !== null
      ? { main: t(m.keyTitle), detail: t(m.keyOn, { prop: propName(t, single, false), layer: layerText(t, single) }) }
      : {
          main: t(m.keysTitle, { count: infos.length }),
          detail: t(m.keysOnProps, {
            count: new Set(infos.map((i) => `${String(i.ctx.line)}:${String(i.ctx.prop)}:${String(i.ctx.layer)}`)).size,
          }),
        };

  const numberValue = (
    part: 'both' | 'in' | 'out',
    labelKey: typeof m.keyValue,
    fieldPath: string,
    disabled = false,
  ) => {
    // The jump's two values sit side by side (`.pairCompact`): their labels go above the inputs (UX-049).
    const inPair = part !== 'both';
    const raw = infos.map((i) => (part === 'in' ? i.ctx.key.vIn : i.ctx.key.vOut) as number);
    const label = { key: m.txKeyValue };
    return (
      <NumberField
        label={t(labelKey)}
        stacked={inPair}
        value={fieldOr(raw.map(setup.toShown), 0)}
        step={setup.step}
        precision={3}
        vars="keyField"
        randomSeed={seed}
        disabled={disabled}
        helpId={fieldPath}
        badges={badges(FIELD.keyValue)}
        {...(part === 'both' ? { help: { key: m.keyValueHelp } } : {})}
        {...setup.unitProps}
        onCommit={(v) => {
          if (v.kind === 'number') {
            const r = setup.toRaw(v.value);
            edit.commit(fieldPath, label, (tx) => {
              // These fields ARE the jump editor ("Arrive at one value, then another"): one-sided writes are what
              // the user asked for here, so `setKeyValues` stays (CR-W3-05-1's KeySection half, recorded REJECT).
              Core.keys.setKeyValues(tx, { keys: ids, part, value: r });
            });
          } else {
            const prog = v.program;
            edit.commit(fieldPath, label, (tx) =>
              exprKeysShown(tx, { keys: ids, part, unit: setup.shown, prog, seed }),
            );
          }
          afterExpr(v);
        }}
        onScrub={(v, final) => {
          const r = setup.toRaw(v);
          edit.scrub(
            fieldPath,
            label,
            (tx) => {
              Core.keys.setKeyValues(tx, { keys: ids, part, value: r });
            },
            final,
          );
        }}
      />
    );
  };

  const shownNow = single === null ? null : (single.ctx.key.vOut as number);

  return (
    <Section
      title={<Title main={header.main} detail={header.detail} />}
      actions={
        <IconButton
          icon="Trash2"
          size="sm"
          label={{ key: m.deleteKey }}
          onClick={() => {
            edit.once({ key: m.txDeleteKeys }, (tx) => {
              Core.keys.deleteKeys(tx, { keys: ids });
            });
          }}
        />
      }
    >
      <div className={s.fields}>
        <Row edit={edit} field={FIELD.keyBeat}>
          <WithInfo info={{ text: m.keyAtHelp, rpe: 'startTime / endTime', community: '关键帧' }}>
            <BeatField
              label={t(m.keyAt)}
              value={at}
              ctx={beatCtx}
              echo
              helpId={FIELD.keyBeat}
              onCommit={(b, relative) => {
                edit.commit(FIELD.keyBeat, { key: m.txKeyBeat }, (tx) => {
                  if (relative) moveKeysBy(tx, { keys: ids, dt: b });
                  else moveKeysTo(tx, { keys: ids, to: b });
                });
              }}
            />
          </WithInfo>
        </Row>

        {valueType === 'number' && !showJump && (
          <Row edit={edit} field={FIELD.keyValue}>
            {numberValue('both', m.keyValue, FIELD.keyValue)}
            {unitMixed && <Readout>{t(m.keyUnitMixed)}</Readout>}
            {single !== null && unit === 'speed' && shownNow !== null && (
              <Readout testId="key-cross-screen">{crossScreenText(t, shownNow)}</Readout>
            )}
            {single !== null && unit === 'thickness' && shownNow !== null && (
              <Readout>{t(m.thicknessPx, { px: fmtNum(Units.thicknessPx(shownNow), 2) })}</Readout>
            )}
          </Row>
        )}
        {valueType === 'rgb' && (
          <Row edit={edit} field={FIELD.keyValue}>
            <ColorField
              label={t(m.keyColour)}
              value={fieldOr(
                infos.map((i) => i.ctx.key.vOut as RGB),
                [255, 255, 255] as RGB,
                rgbEq,
              )}
              helpId={FIELD.keyValue}
              onCommit={(c) => {
                if (c === null) return;
                edit.commit(FIELD.keyValue, { key: m.txKeyValue }, (tx) => {
                  Core.keys.setKeyValues(tx, { keys: ids, part: 'both', value: c });
                });
              }}
            />
          </Row>
        )}
        {valueType === 'string' && (
          <Row edit={edit} field={FIELD.keyValue}>
            <TextArea
              label={t(m.keyText)}
              value={fieldOf(infos.map((i) => i.ctx.key.vOut as string)) ?? ''}
              rows={2}
              helpId={FIELD.keyValue}
              onCommit={(text) => {
                edit.commit(FIELD.keyValue, { key: m.txKeyValue }, (tx) => {
                  Core.keys.setKeyValues(tx, { keys: ids, part: 'both', value: text });
                });
              }}
            />
          </Row>
        )}
        {valueType === 'mixed' && <Readout>{t(m.keyMixedTypes)}</Readout>}

        {/* ---------------- Jump (single number key) */}
        {single !== null && valueType === 'number' && (
          <Row edit={edit} field={FIELD.keyJump}>
            <WithInfo info={{ text: m.keyJumpInfo }}>
              <Checkbox
                className={s.checkInline}
                label={t(m.keyJumpLabel)}
                checked={isJump || jumpOpen}
                helpId={FIELD.keyJump}
                disabledReason={single.ctx.index === 0 ? { key: m.keyJumpFirst } : null}
                onChange={(on) => {
                  if (on) {
                    setJumpOpen(true);
                    return;
                  }
                  setJumpOpen(false);
                  if (isJump) {
                    edit.once({ key: m.txKeyJump }, (tx) => {
                      Core.keys.removeJump(tx, { keys: ids, keep: 'in' });
                    });
                  }
                }}
              />
            </WithInfo>
            {showJump && (
              <div className={s.pairCompact}>
                {numberValue('in', m.keyArrive, `${FIELD.keyValue}.in`, arriveFixed)}
                {numberValue('out', m.keyLeave, `${FIELD.keyValue}.out`)}
              </div>
            )}
            {showJump && arriveFixed && <Readout>{t(m.keyArriveFixed)}</Readout>}
          </Row>
        )}

        {ignoredByKind && <Callout tone="warning">{t(m.keyIgnoredByKind)}</Callout>}

        {/* ---------------- Outgoing segment of a single key */}
        {single !== null &&
          (single.ctx.next === null ? (
            <Readout testId="last-key">{t(m.keyLast)}</Readout>
          ) : (
            <Section
              title={
                <Title
                  main={t(m.segmentTitle)}
                  detail={t(m.segmentSpan, {
                    from: base.fmtBeat(single.ctx.key.t),
                    to: base.fmtBeat(single.ctx.next.t),
                  })}
                />
              }
              level={4}
            >
              <SegmentBody services={services} store={store} segments={ids} />
            </Section>
          ))}
      </div>
    </Section>
  );
}

/** "L0", "Layer 2" or the layer's own name (UX-039 / CR-W3-05-7: layer 0 is "L0", matching the curve chips). */
function layerText(t: ReturnType<typeof useT>, info: KeyInfo): string {
  if (info.layerName !== null && info.layerName !== '') return info.layerName;
  return info.ctx.layer === 0 ? t(m.layerBase) : t(m.layerN, { n: info.ctx.layer });
}

export const keySectionDef: InspectorSectionDef = {
  id: 'key',
  order: 210,
  level: 'guided',
  appliesTo: (sel) => sel.keys.size > 0,
  component: KeySection,
};
